import { useEffect, useState } from "react";
import { useSocket } from "../contexts/SocketContext";
import { CheckCircle, AlertCircle, Loader, Users, UserMinus, Ban, AlertTriangle } from "lucide-react";

export default function UploadProgress({ contactList, onComplete }) {
  const { on, off } = useSocket();
  const [progress, setProgress] = useState({
    processed: 0,
    total: contactList?.row_count || 0,
    pct: 0,
  });
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState(contactList?.status || "pending");

  useEffect(() => {
    if (!contactList?.id) return;

    const handleProgress = (data) => {
      if (data.contact_list_id !== contactList.id) return;
      setProgress({
        processed: data.processed,
        total: data.total,
        pct: data.pct,
      });
    };

    const handleComplete = (data) => {
      if (data.id !== contactList.id) return;
      setStatus("completed");
      setStats({
        valid: data.valid_count,
        duplicates: data.duplicate_count,
        optedOut: data.opt_out_count,
        landlines: data.landline_count,
        errors: data.error_count,
      });
      onComplete?.(data);
    };

    on("upload_progress", handleProgress);
    on("upload_complete", handleComplete);

    return () => {
      off("upload_progress", handleProgress);
      off("upload_complete", handleComplete);
    };
  }, [contactList?.id, on, off, onComplete]);

  if (!contactList) return null;

  const isProcessing = status === "processing" || status === "pending";
  const isComplete = status === "completed";
  const isFailed = status === "failed";

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        {isProcessing && <Loader className="w-5 h-5 text-blue-500 animate-spin" />}
        {isComplete && <CheckCircle className="w-5 h-5 text-green-500" />}
        {isFailed && <AlertCircle className="w-5 h-5 text-red-500" />}
        <h4 className="text-sm font-semibold text-gray-700">
          {isProcessing && "Processing contacts..."}
          {isComplete && "Upload complete!"}
          {isFailed && "Upload failed"}
        </h4>
        <span className="text-xs text-gray-400 ml-auto">{contactList.name}</span>
      </div>

      {/* Progress bar */}
      {isProcessing && (
        <div className="mb-3">
          <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all duration-300"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {progress.processed} / {progress.total} rows processed ({progress.pct}%)
          </p>
        </div>
      )}

      {/* Completion stats */}
      {isComplete && stats && (
        <div className="grid grid-cols-2 gap-3 mt-2">
          <StatCard icon={Users} color="green" label="Valid contacts" value={stats.valid} />
          <StatCard icon={AlertTriangle} color="amber" label="Duplicates" value={stats.duplicates} />
          <StatCard icon={UserMinus} color="red" label="Opted out (DNC)" value={stats.optedOut} />
          <StatCard icon={Ban} color="gray" label="Parse errors" value={stats.errors} />
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  const colors = {
    green: "text-green-600 bg-green-50",
    amber: "text-amber-600 bg-amber-50",
    red: "text-red-600 bg-red-50",
    gray: "text-gray-600 bg-gray-50",
  };

  return (
    <div className={`rounded-lg p-2.5 ${colors[color]}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <span className="text-lg font-bold">{value}</span>
      </div>
      <p className="text-xs mt-0.5">{label}</p>
    </div>
  );
}
